/* ════════════ THE UMBILICAL ════════════
   The big tech page's atmosphere: rounded-square app icons rain
   down slowly, each trailing a thin line back up to the top of
   the screen. The download finished years ago. The upload never
   does. Now and then one flashes steel: syncing. Always syncing. */

import { onPage } from "./router";

const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;

function umbilical(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#umbilical")!;
  const ctx = c.getContext("2d")!;

  const STEEL = "#c9d4e3";
  const GREY = "#9aa0a6";

  type App = {
    x: number;
    y: number;
    vy: number;
    r: number; // half-size of the icon square
    col: string;
    sync: number; // frames until next sync flash
    flash: number; // frames of flash remaining
  };
  let apps: App[] = [];

  const spawn = (atTop = false): App => {
    const isHot = Math.random() < 0.18;
    return {
      x: Math.random() * c.width,
      y: atTop ? -20 : Math.random() * c.height,
      vy: 0.12 + Math.random() * 0.22,
      r: isHot ? 8 + Math.random() * 6 : 5 + Math.random() * 5,
      col: isHot ? STEEL : GREY,
      sync: 240 + Math.random() * 600,
      flash: 0,
    };
  };

  const seed = () => {
    c.width = innerWidth;
    c.height = innerHeight;
    const count = Math.min(22, Math.floor((c.width * c.height) / 80000));
    apps = Array.from({ length: count }, () => spawn());
  };
  seed();
  addEventListener("resize", seed, { signal });

  const draw = () => {
    ctx.clearRect(0, 0, c.width, c.height);
    for (const a of apps) {
      const hot = a.col === STEEL;
      const base = hot ? 0.45 : 0.18;
      const alpha = a.flash > 0 ? Math.min(0.7, base + 0.3) : base;

      // the umbilical: a thin line from the icon up to the top edge
      ctx.globalAlpha = alpha * 0.35;
      ctx.strokeStyle = a.col;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y - a.r);
      ctx.lineTo(a.x, 0);
      ctx.stroke();

      // the app icon: rounded square, occasionally flashing a sync
      ctx.globalAlpha = alpha;
      ctx.beginPath();
      ctx.roundRect(a.x - a.r, a.y - a.r, a.r * 2, a.r * 2, a.r * 0.4);
      ctx.stroke();
      if (a.flash > 0) {
        ctx.globalAlpha = alpha * 0.3;
        ctx.fillStyle = a.col;
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
  };

  if (REDUCED) {
    draw();
    return;
  }
  const tick = () => {
    if (signal.aborted) return;
    for (let i = 0; i < apps.length; i++) {
      const a = apps[i]!;
      a.y += a.vy;
      a.sync--;
      if (a.flash > 0) a.flash--;
      if (a.sync <= 0) {
        a.flash = 14;
        a.sync = 240 + Math.random() * 600;
      }
      if (a.y - a.r > c.height + 4) apps[i] = spawn(true);
    }
    draw();
    requestAnimationFrame(tick);
  };
  tick();
}

onPage(umbilical);
